import { useRef, useCallback, useMemo } from "react";
import {
  DTFocusAction,
  DTFocusDispatchContext,
  DTFocusState,
  DTFocusStateContext,
} from "./DTFocusContext";
import { CellPosition } from "../../../../internal/types";
import {
  areCellPositionsEqual,
  formatCellPositionString,
} from "../../util/cellPositionHelpers";
import { scrollCellIntoView } from "../../../../internal/scrollCellIntoView";

const initialState: DTFocusState = {
  focusedCell: null,
  focusRestorePoint: { rowIndex: 0, columnIndex: 0 },
  hasFocusBeenDisrupted: false,
};

function reducer(
  state: DTFocusState,
  action: DTFocusAction,
  rowCount: number,
  columnCount: number,
): DTFocusState {
  switch (action.type) {
    case "FOCUS_CELL_MANUALLY": {
      return {
        ...state,
        focusedCell: action.payload.cellPosition,
        hasFocusBeenDisrupted: false,
      };
    }
    case "FOCUS_CELL": {
      return {
        ...state,
        focusedCell: action.payload.cellPosition,
        focusRestorePoint: action.payload.cellPosition,
        hasFocusBeenDisrupted: false,
      };
    }
    case "FOCUS_TABLE": {
      return {
        ...state,
        // Focus the focus restore point (i.e. the last focused cell when the table was blurred or 0,0 if never focused)
        focusedCell: state.focusRestorePoint,
      };
    }
    // Move to the next cell in the given direction
    case "MOVE_FOCUS": {
      let newFocusedCell: CellPosition;

      const currentColumnIndex = state.focusedCell?.columnIndex ?? 0;
      const currentRowIndex = state.focusedCell?.rowIndex ?? 0;

      if (action.payload.direction === "left") {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: Math.max(0, currentColumnIndex - 1),
        };
      } else if (action.payload.direction === "right") {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: Math.min(columnCount - 1, currentColumnIndex + 1),
        };
      } else if (action.payload.direction === "up") {
        newFocusedCell = {
          rowIndex: Math.max(0, currentRowIndex - 1),
          columnIndex: currentColumnIndex,
        };
      } else if (action.payload.direction === "down") {
        newFocusedCell = {
          rowIndex: Math.min(rowCount - 1, currentRowIndex + 1),
          columnIndex: currentColumnIndex,
        };
      } else {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: currentColumnIndex,
        };
      }
      return {
        ...state,
        focusedCell: newFocusedCell,
      };
    }
    // Jump to the first/last cell in the given direction
    case "JUMP_FOCUS": {
      let newFocusedCell: CellPosition;
      const currentColumnIndex = state.focusedCell?.columnIndex ?? 0;
      const currentRowIndex = state.focusedCell?.rowIndex ?? 0;
      if (action.payload.direction === "left") {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: 0,
        };
      } else if (action.payload.direction === "right") {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: columnCount - 1,
        };
      } else if (action.payload.direction === "up") {
        newFocusedCell = {
          rowIndex: 0,
          columnIndex: currentColumnIndex,
        };
      } else if (action.payload.direction === "down") {
        newFocusedCell = {
          rowIndex: rowCount - 1,
          columnIndex: currentColumnIndex,
        };
      } else {
        newFocusedCell = {
          rowIndex: currentRowIndex,
          columnIndex: currentColumnIndex,
        };
      }
      return {
        ...state,
        focusedCell: newFocusedCell,
      };
    }
    case "BLUR": {
      return {
        ...state,
        focusedCell: null,
        focusRestorePoint: state.focusedCell ?? { columnIndex: 0, rowIndex: 0 },
        hasFocusBeenDisrupted: false,
      };
    }
    case "FOCUS_SUB_COMPONENT": {
      return {
        ...state,
        hasFocusBeenDisrupted: true,
      };
    }
    case "BLUR_SUB_COMPONENT": {
      // Meaningless action, but have it if we need it
      return {
        ...state,
      };
    }
    default: {
      throw new Error("Unknown action");
    }
  }
}

export interface DTFocusProviderProps {
  children: React.ReactNode;
  rowCount: number;
  columnCount: number;
  tableRef: React.RefObject<HTMLDivElement>;
  leftPinnedWidth: number;
  rightPinnedWidth: number;
}

export function DTFocusProvider({
  children,
  rowCount,
  columnCount,
  tableRef,
  leftPinnedWidth,
  rightPinnedWidth,
}: DTFocusProviderProps) {
  const stateRef = useRef<DTFocusState>(initialState);
  const dispatch = useCallback(
    (action: DTFocusAction) => {
      const newState = reducer(stateRef.current, action, rowCount, columnCount);
      let shouldSkipFocusUpdate = false;
      // If the focused cell is the same as the current focused cell, skip the update/focus
      if (
        areCellPositionsEqual(
          newState.focusedCell,
          stateRef.current.focusedCell,
        )
      ) {
        shouldSkipFocusUpdate = true;
        if (action.type === "FOCUS_TABLE" && newState.hasFocusBeenDisrupted) {
          shouldSkipFocusUpdate = false;
        }
      }
      stateRef.current = newState;

      // If there is no new cell to focus, skip focusing.
      if (!newState.focusedCell || shouldSkipFocusUpdate) {
        return;
      }

      const element = tableRef.current?.querySelector(
        `[data-cell-pos="${formatCellPositionString(newState.focusedCell)}"]`,
      ) as HTMLElement | null;

      if (element) {
        element.focus();
        scrollCellIntoView(element, {
          leftWidth: leftPinnedWidth,
          rightWidth: rightPinnedWidth,
        });
      } else {
        // Focus moved to a non-existent cell. This is possible if rows per page was reduced.
        dispatch({
          type: "FOCUS_CELL_MANUALLY",
          payload: { cellPosition: { rowIndex: 0, columnIndex: 0 } },
        });
      }
    },
    [rowCount, columnCount, tableRef, leftPinnedWidth, rightPinnedWidth],
  );

  const state = useMemo(
    () => ({ getFocusedCell: () => stateRef.current.focusedCell }),
    [],
  );

  return (
    <DTFocusStateContext.Provider value={state}>
      <DTFocusDispatchContext.Provider value={dispatch}>
        {children}
      </DTFocusDispatchContext.Provider>
    </DTFocusStateContext.Provider>
  );
}
